// src/ine/ine_config/pages/InePartsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import AutoDismissAlert from "../../../components/autoDismiss";
import { API_URLS } from "../../../api/apiConfig";
import {
  getWithAuth,
  postWithAuth,
  putWithAuth,
  deleteWithAuth,
} from "../../../api/api";
import { Button, Modal, Form } from "react-bootstrap";

/* ====== Config / Constantes ====== */
const STATUS_BADGE_MAP = {
  PENDING: "badge bg-warning",
  PROCESSING: "badge bg-info text-dark",
  SUCCESS: "badge bg-success",
  SENT: "badge bg-success",
  ERROR: "badge bg-danger",
};

/* ====== Página ====== */
export default function InePartsPage() {
  // lista + paginación en cliente
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const pageItems = useMemo(() => {
    const start = (page - 1) * limit;
    return items.slice(start, start + limit);
  }, [items, page, limit]);

  // ui/alerts
  const [isLoading, setIsLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState("");
  const [alertType, setAlertType] = useState("info");

  // crear (desde botón header)
  const [showCreate, setShowCreate] = useState(false);

  function openAlert(msg, type = "info") {
    setMessage(msg);
    setAlertType(type);
    setShowAlert(true);
  }

  async function fetchData() {
    setIsLoading(true);
    try {
      const resp = await getWithAuth(API_URLS.LIST_INE_PART_SENDING);
      if (resp?.message === "success") {
        const list = Array.isArray(resp?.data) ? resp.data : [];
        list.sort((a, b) => {
          const da = new Date(a?.created_at || 0).getTime();
          const db = new Date(b?.created_at || 0).getTime();
          return db - da;
        });
        setItems(list);
        setTotal(list.length);
        setPage((p) =>
          Math.min(p, Math.max(1, Math.ceil(list.length / limit)))
        );
      } else {
        openAlert(
          resp?.detail || "No se pudieron cargar los partes INE",
          "danger"
        );
      }
    } catch (e) {
      console.error(e);
      openAlert("Ha ocurrido un error conectando con el servidor", "danger");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="py-3">
      {/* Alerts */}
      {showAlert && (
        <div className="mb-4">
          <AutoDismissAlert
            message={message}
            alertType={alertType}
            onClose={() => setShowAlert(false)}
          />
        </div>
      )}

      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="fw-bold mb-1">Partes INE</h4>
          <p className="text-muted mb-0">
            Histórico de partes creados/enviados.
          </p>
        </div>
        <Button variant="primary" size="sm" onClick={() => setShowCreate(true)}>
          Nuevo parte
        </Button>
      </div>

      {/* Tabla */}
      <div className="table-responsive" style={{ overflowX: "auto" }}>
        <table className="table align-middle table-row-dashed fs-6 gy-4">
          <thead>
            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
              <th style={{ minWidth: 140 }}>Creación</th>
              <th style={{ minWidth: 110 }}>Periodo</th>
              <th style={{ minWidth: 120 }}>Estado</th>
              <th style={{ minWidth: 80 }} className="text-center">
                XML
              </th>
              <th style={{ minWidth: 120 }} className="text-end">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {isLoading ? (
              <tr>
                <td colSpan="5" className="text-center py-5">
                  <div
                    className="spinner-border text-color-amarillo-livensa"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  />
                </td>
              </tr>
            ) : pageItems.length > 0 ? (
              pageItems.map((row) => {
                const id = row?.id || row?._id || "-";
                const createdAt = formatDate(row?.created_at); // solo fecha
                const period =
                  row?.period ?? toYYYYMM(row?.year, row?.month) ?? "-";
                const status = (row?.status || "PENDING").toUpperCase();
                const badge = STATUS_BADGE_MAP[status] || "badge bg-secondary";
                const hasXml = !!row?.s3_xml_url;
                return (
                  <tr key={id}>
                    <td>{createdAt}</td>
                    <td>{period}</td>
                    <td>
                      <span className={badge}>{humanStatus(status)}</span>
                    </td>

                    <td className="text-center">
                      {hasXml ? (
                        <a
                          href={row.s3_xml_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm  mx-2"
                          title="Descargar XML"
                        >
                          <i className="icon-in-button-color fa-solid fa-download text-amarillo-livensa"></i>
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>

                    <td className="text-end">
                      <TableActions
                        row={row}
                        fetchData={fetchData}
                        openAlert={openAlert}
                      />
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-5">
                  No hay partes todavía.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="row mt-4">
        <div className="col-sm-12 col-md-7 d-flex align-items-center justify-content-center justify-content-md-end gap-3">
          <Form.Select
            style={{ width: 180 }}
            value={limit}
            onChange={(e) => {
              setPage(1);
              setLimit(Number(e.target.value));
            }}
          >
            <option value="5">5 por página</option>
            <option value="10">10 por página</option>
            <option value="20">20 por página</option>
            <option value="50">50 por página</option>
          </Form.Select>

          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                <button
                  className="page-link"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Anterior
                </button>
              </li>
              <li className="page-item disabled">
                <span className="page-link">
                  Página {page} de {totalPages}
                </span>
              </li>
              <li
                className={`page-item ${page >= totalPages ? "disabled" : ""}`}
              >
                <button
                  className="page-link"
                  onClick={() => setPage((p) => (p < totalPages ? p + 1 : p))}
                >
                  Siguiente
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Modal: Crear (contenido dentro del mismo archivo) */}
      {showCreate && (
        <ModalInePartCreate
          show={showCreate}
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            fetchData();
            openAlert("Parte INE creado correctamente.", "success");
          }}
        />
      )}
    </div>
  );
}

/* ====== TableActions con dropdown estilo Blocks + Modales internos ====== */
function TableActions({ row, fetchData, openAlert }) {
  const [showMenu, setShowMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const menuRef = useRef(null);
  const anchorRef = useRef(null);

  // modales locales
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const toggleDropdown = (e) => {
    e.preventDefault();
    setShowMenu((s) => !s);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !anchorRef.current.contains(event.target)
      ) {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMenu]);

  useEffect(() => {
    if (showMenu && anchorRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const elementWidth = anchorRef.current.offsetWidth;
      const screenWidth = window.innerWidth;
      const menuX = anchorRect.left - screenWidth + elementWidth;
      const menuY = anchorRect.top + anchorRect.height + 10;
      setMenuPosition({ x: menuX, y: menuY });
    }
  }, [showMenu]);

  const status = (row?.status || "").toUpperCase();
  const disabledEdit = status === "SENT";
  const disabledDelete = status === "SENT" || status === "ERROR";

  return (
    <>
      <a
        href="#"
        className="btn btn-sm btn-light btn-flex btn-center btn-active-light-primary show menu-dropdown"
        onClick={toggleDropdown}
        data-kt-menu-trigger="click"
        data-kt-menu-placement="bottom-end"
        ref={anchorRef}
      >
        Acciones
        <i className="ki-outline ki-down text-color-azul-livensa fs-5 ms-1"></i>
      </a>

      <div
        ref={menuRef}
        style={{
          transform: `translate3d(${menuPosition.x}px, ${menuPosition.y}px, 0)`,
        }}
        className={`menu-actions-table menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-semibold fs-7 w-150px py-4 ${
          showMenu ? "show" : ""
        }`}
        data-kt-menu="true"
        data-popper-placement="bottom-end"
      >
        <div className="menu-item px-3">
          <a
            onClick={() => {
              setShowMenu(false);
              setShowEdit(true);
            }}
            className={`menu-link px-3 ${disabledEdit ? "disabled" : ""}`}
            title={disabledEdit ? "No editable si está Enviado" : "Editar"}
          >
            Actualizar
          </a>
        </div>
        <div className="menu-item px-3">
          <a
            onClick={() => {
              setShowMenu(false);
              setShowDelete(true);
            }}
            className={`menu-link px-3 ${disabledDelete ? "disabled" : ""}`}
            title={
              disabledDelete
                ? "No se puede eliminar Enviado o Error"
                : "Eliminar"
            }
          >
            Eliminar
          </a>
        </div>
      </div>

      {/* Modales embebidos */}
      {showEdit && (
        <ModalInePartUpdate
          show={showEdit}
          onClose={() => setShowEdit(false)}
          row={row}
          onSuccess={() => {
            setShowEdit(false);
            fetchData();
            openAlert("Parte INE actualizado correctamente.", "success");
          }}
        />
      )}

      {showDelete && (
        <ModalInePartDelete
          show={showDelete}
          onClose={() => setShowDelete(false)}
          row={row}
          onSuccess={() => {
            setShowDelete(false);
            fetchData();
            openAlert("Parte INE eliminado.", "success");
          }}
        />
      )}
    </>
  );
}

/* ====== Modales internos ====== */

// Crear
function ModalInePartCreate({ show, onClose, onSuccess }) {
  const { prevMonth, prevYear } = useMemo(() => getPrevMonthYear(), []);
  const [month, setMonth] = useState(prevMonth);
  const [year, setYear] = useState(prevYear);
  const [ctrl, setCtrl] = useState("");
  const [orderNumber, setOrderNumber] = useState("");
  const [saving, setSaving] = useState(false);

  const isValidMonth = Number.isInteger(month) && month >= 1 && month <= 12;
  const isValidYear =
    Number.isInteger(year) &&
    year >= 2000 &&
    year <= new Date().getFullYear() + 1;

  const canSave =
    isValidMonth && isValidYear && !!String(orderNumber).trim() && !saving;

  async function handleCreate() {
    if (!canSave) return;
    setSaving(true);
    try {
      const payload = {
        month,
        year,
        control_number: ctrl, // ← sin toUpper ni trim obligatorio
        order_number: String(orderNumber).trim(),
      };
      const resp = await postWithAuth(
        API_URLS.CREATE_INE_PART_SENDING,
        payload
      );
      if (resp?.message === "success") onSuccess?.();
      else alert(humanizeError(resp?.detail) || "No se pudo crear el parte.");
    } catch {
      alert("Error al crear el parte.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Nuevo parte INE</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleCreate();
          }}
        >
          <RowMonthYearCtrl
            month={month}
            setMonth={setMonth}
            year={year}
            setYear={setYear}
            ctrl={ctrl}
            setCtrl={setCtrl} // ← pasa setter directo
            orderNumber={orderNumber}
            setOrderNumber={setOrderNumber}
          />
          <div className="mt-3 small">
            {!isValidMonth && <div className="text-danger">Mes inválido.</div>}
            {!isValidYear && <div className="text-danger">Año inválido.</div>}
            {/* sin mensajes sobre ctrl */}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={!canSave}>
          {saving ? "Creando…" : "Crear"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Editar
function ModalInePartUpdate({ show, onClose, row, onSuccess }) {
  const [month, setMonth] = useState(Number(row?.month) || 1);
  const [year, setYear] = useState(
    Number(row?.year) || new Date().getFullYear()
  );
  const [ctrl, setCtrl] = useState(
    // coge el que venga del backend; compatible con `control_digit` o `control_number`
    (row?.control_number ?? row?.control_digit ?? "").toString()
  );
  const [orderNumber, setOrderNumber] = useState(
    (row?.order_number ?? "").toString()
  );
  const [saving, setSaving] = useState(false);

  const status = (row?.status || "").toUpperCase();
  const locked = status === "SENT";

  const isValidMonth = Number.isInteger(month) && month >= 1 && month <= 12;
  const isValidYear =
    Number.isInteger(year) &&
    year >= 2000 &&
    year <= new Date().getFullYear() + 1;
  const canSave =
    !locked &&
    isValidMonth &&
    isValidYear &&
    !!String(orderNumber).trim() &&
    !saving;

  async function handleUpdate() {
    if (!canSave) return;
    setSaving(true);
    try {
      const id = row?.id || row?._id;
      const payload = {
        month,
        year,
        control_number: ctrl || "",
        order_number: String(orderNumber).trim(),
      };
      const resp = await putWithAuth(
        API_URLS.UPDATE_INE_PART_SENDING(id),
        payload
      );
      if (resp?.message === "success") onSuccess?.();
      else
        alert(humanizeError(resp?.detail) || "No se pudo actualizar el parte.");
    } catch (e) {
      console.error(e);
      alert("Error al actualizar el parte.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Editar parte INE</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {locked && (
          <div className="alert alert-warning py-2">
            Este parte está Enviado y no se puede editar.
          </div>
        )}
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdate();
          }}
        >
          <RowMonthYearCtrl
            month={month}
            setMonth={setMonth}
            year={year}
            setYear={setYear}
            ctrl={ctrl}
            setCtrl={(v) => setCtrl(v)}
            orderNumber={orderNumber}
            setOrderNumber={setOrderNumber}
            disabled={locked}
          />
          <div className="mt-3 small">
            {!isValidMonth && <div className="text-danger">Mes inválido.</div>}
            {!isValidYear && <div className="text-danger">Año inválido.</div>}
            {!String(orderNumber).trim() && (
              <div className="text-danger">
                El número de orden es obligatorio.
              </div>
            )}
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cerrar
        </Button>
        <Button variant="primary" onClick={handleUpdate} disabled={!canSave}>
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// Eliminar (confirm)
function ModalInePartDelete({ show, onClose, row, onSuccess }) {
  const [deleting, setDeleting] = useState(false);
  const status = (row?.status || "").toUpperCase();
  const locked = status === "SENT" || status === "ERROR";

  async function handleDelete() {
    if (locked) return;
    setDeleting(true);
    try {
      const id = row?.id || row?._id;
      const resp = await deleteWithAuth(API_URLS.DELETE_INE_PART_SENDING(id));
      if (resp?.message === "success") onSuccess?.();
      else
        alert(humanizeError(resp?.detail) || "No se pudo eliminar el parte.");
    } catch (e) {
      console.error(e);
      alert("Error al eliminar el parte.");
    } finally {
      setDeleting(false);
    }
  }

  const period = toYYYYMM(row?.year, row?.month) || "-";

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Eliminar parte INE</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        ¿Seguro que deseas eliminar el parte <code>{period}</code>?
        {locked && (
          <div className="mt-2 text-danger small">
            No es posible eliminar un parte Enviado o en Error.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="danger"
          onClick={handleDelete}
          disabled={locked || deleting}
        >
          {deleting ? "Eliminando…" : "Eliminar"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ====== Subcomponentes y helpers ====== */
function RowMonthYearCtrl({
  month,
  setMonth,
  year,
  setYear,
  ctrl,
  setCtrl,
  orderNumber,
  setOrderNumber,
  disabled = false,
}) {
  return (
    <div className="row row-cols-auto g-3 align-items-center">
      <Form.Group className="col-auto d-flex align-items-center">
        <Form.Label className="mb-0 me-2">Mes</Form.Label>
        <Form.Select
          value={month}
          onChange={(e) => setMonth(Number(e.target.value))}
          style={{ minWidth: 100 }}
          disabled={disabled}
        >
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>
              {m.toString().padStart(2, "0")}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="col-auto d-flex align-items-center">
        <Form.Label className="mb-0 me-2">Año</Form.Label>
        <Form.Control
          type="number"
          value={year}
          onChange={(e) => setYear(Number(e.target.value))}
          style={{ width: 110 }}
          min={2000}
          max={new Date().getFullYear() + 1}
          disabled={disabled}
        />
      </Form.Group>

      <Form.Group className="col-auto d-flex align-items-center">
        <Form.Label className="mb-0 me-2">Dígito control</Form.Label>
        <Form.Control
          type="text"
          value={ctrl}
          onChange={(e) => setCtrl(e.target.value)}
          style={{ width: 200 }}
          placeholder=""
          disabled={disabled}
        />
      </Form.Group>
      <Form.Group className="col-auto d-flex align-items-center">
        <Form.Label className="mb-0 me-2">Nº orden</Form.Label>
        <Form.Control
          type="text"
          value={orderNumber}
          onChange={(e) => setOrderNumber(e.target.value)}
          style={{ width: 180 }}
          placeholder=""
          disabled={disabled}
        />
      </Form.Group>
    </div>
  );
}

function formatDate(v) {
  if (!v) return "-";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  // SOLO FECHA
  return d.toLocaleDateString("es-ES", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}
function toYYYYMM(year, month) {
  if (!year || !month) return null;
  const m = String(month).padStart(2, "0");
  return `${year}-${m}`;
}
function getPrevMonthYear() {
  const now = new Date();
  const firstThis = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastPrev = new Date(firstThis.getTime() - 1);
  return {
    prevYear: lastPrev.getFullYear(),
    prevMonth: lastPrev.getMonth() + 1,
  };
}
function humanStatus(s) {
  switch ((s || "").toUpperCase()) {
    case "PENDING":
      return "Pendiente";
    case "PROCESSING":
      return "Procesando";
    case "SUCCESS":
    case "SENT":
      return "Enviado";
    case "ERROR":
      return "Error";
    default:
      return s;
  }
}
function humanizeError(detail) {
  if (!detail) return null;
  const map = {
    INE_PART_SENDING_ALREADY_EXISTS: "Ya existe un parte para ese mes y año.",
    CANNOT_UPDATE_SENT_INE_PART_SENDING:
      "No se puede modificar o eliminar un parte Enviado.",
    SOMETHING_WRONG: "Ha ocurrido un error inesperado.",
  };
  return map[detail] || detail;
}
